import React from "react";
import Select from "react-select";
import api from "../api/client";
import { useOrg } from "../context/OrgContext";
import { useTerminology } from "../context/TerminologyContext";
import { useAuth } from "../context/AuthContext";
import { DateRangePicker, LotSelectionTable } from "../components/ui-helpers";
import { DetailsModal, ExpandableChart } from "../components/chart-helpers";
import { downloadCsvRows, downloadExcelRows } from "../utils/spreadsheet";
import { formatDateTimeForExport } from "../utils/datetime";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

const uniq = (arr) => Array.from(new Set((arr || []).filter(Boolean)));
const palette = [
  "#8884d8",
  "#82ca9d",
  "#ffc658",
  "#ff8042",
  "#8dd1e1",
  "#a4de6c",
  "#d0ed57",
  "#d88884",
  "#84a1d8",
  "#ca82ac",
];
const pickColor = (i) => palette[i % palette.length];
const STATUSES = [
  "Approved",
  "Rejected",
  "Invalidated",
  "Initiated",
  "Pending",
];
const KPI_TABS = [
  { key: "status", label: "KPI by Status" },
  { key: "yield", label: "KPI by Yield" },
  { key: "deviations", label: "KPI by Deviations" },
];
const emptyDateRange = { date_from: "", date_to: "" };

const rsStyles = {
  control: (base) => ({
    ...base,
    minHeight: 36,
    borderRadius: 8,
    boxShadow: "none",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 36 }),
  multiValue: (base) => ({ ...base, borderRadius: 6 }),
  menu: (base) => ({ ...base, zIndex: 20 }),
};

function cleanFilterValue(value) {
  return String(value ?? "")
    .normalize("NFKC")
    .trim();
}
function cleanTextKey(value) {
  return cleanFilterValue(value).replace(/\s+/g, " ").toLowerCase();
}
function skuKey(value) {
  return cleanFilterValue(value)
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();
}
function realMaterialId(row) {
  const mid = cleanFilterValue(row?.material_id);
  return mid && !mid.includes("::") ? mid : "";
}
function normalizeMaterialRow(row, source = "catalog") {
  return {
    vendor: cleanFilterValue(
      row?.vendor || row?.Vendor || row?.["Vendor Name"],
    ),
    material: cleanFilterValue(
      row?.material || row?.Material || row?.["Material Name"],
    ),
    sku: cleanFilterValue(row?.sku || row?.SKU),
    material_id: realMaterialId(row),
    _source: source,
  };
}
function rowIdentityKey(row) {
  const mid = realMaterialId(row);
  if (mid) return `id:${mid}`;
  return `legacy:${cleanTextKey(row?.vendor)}::${cleanTextKey(row?.material)}::${skuKey(row?.sku)}`;
}
function mergeMaterialRows(catalogRows = [], statusRows = []) {
  const out = [];
  const seen = new Set();
  [
    ...(catalogRows || []).map((r) => normalizeMaterialRow(r, "catalog")),
    ...(statusRows || []).map((r) => normalizeMaterialRow(r, "status")),
  ].forEach((row) => {
    if (!row.vendor || !row.material || !row.sku) return;
    const key = rowIdentityKey(row);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(row);
  });
  return out;
}
function dateRangeComplete(value) {
  return Boolean(
    value?.date_from && value?.date_to && value.date_to >= value.date_from,
  );
}
function useStatusRows(user) {
  const [rows, setRows] = React.useState([]);
  const { current: currentOrg } = useOrg();
  React.useEffect(() => {
    const vf = user?.currentRole === "Vendor" ? user.uid : undefined;
    const isSAorVendor =
      user?.currentRole === "Super Admin" || user?.currentRole === "Vendor";
    const params = { vendor: vf || undefined };
    if (isSAorVendor && currentOrg) params.org = currentOrg;

    let alive = true;
    api
      .get("/status_table", { params })
      .then((res) => {
        if (alive) setRows(res.data?.rows || []);
      })
      .catch(() => {
        if (alive) setRows([]);
      });
    return () => {
      alive = false;
    };
  }, [user, currentOrg]);
  return rows;
}

function latestMaterialTips(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const replacedIds = new Set(
    list
      .map((row) => String(row?.predecessor_material_id || "").trim())
      .filter(Boolean),
  );
  if (!replacedIds.size) return list;
  return list.filter((row) => !replacedIds.has(String(row?.material_id || "").trim()));
}

function isApprovedMaterialRow(row) {
  const status = cleanFilterValue(row?.approval_status || row?.status).toLowerCase();
  if (status) return status === "approved";
  const approved = cleanFilterValue(row?.approved).toLowerCase();
  return ["1", "true", "yes", "active", "approved"].includes(approved);
}

function approvedLatestMaterialRows(rows) {
  return latestMaterialTips(rows).filter(isApprovedMaterialRow);
}

function useMaterialCatalog(user) {
  const [rows, setRows] = React.useState([]);
  const { current: currentOrg } = useOrg();
  React.useEffect(() => {
    let alive = true;
    const params = { approved_only: true, latest_only: true };
    if (user?.currentRole === "Vendor" && user?.uid) params.vendor = user.uid;
    api
      .get("/materials/catalog", { params })
      .then((res) => {
        if (alive) setRows(approvedLatestMaterialRows(res.data?.materials || []));
      })
      .catch(() => {
        api
          .get("/manager/list_vendor_materials", { params: { latest_only: true } })
          .then((res) => {
            if (!alive) return;
            const byVendor = res.data?.materials_by_vendor || {};
            setRows(approvedLatestMaterialRows(Object.values(byVendor).flat()));
          })
          .catch(() => {
            if (alive) setRows([]);
          });
      });
    return () => {
      alive = false;
    };
  }, [user?.currentRole, user?.uid, currentOrg]);
  return rows;
}
function useDebouncedFetch(enabled, deps, fn, delay = 300) {
  React.useEffect(() => {
    if (!enabled) return undefined;
    const ctrl = new AbortController();
    const t = setTimeout(() => {
      fn(ctrl.signal);
    }, delay);
    return () => {
      ctrl.abort();
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
function usePrunedSelection(ids, setIds, rows) {
  React.useEffect(() => {
    const valid = new Set((rows || []).map(rowIdentityKey));
    const next = (ids || []).filter((id) => valid.has(String(id)));
    if (
      next.length !== (ids || []).length ||
      next.some((id, idx) => id !== ids[idx])
    )
      setIds(next);
  }, [ids, setIds, rows]);
}
function selectedRowsFromIds(rows, ids) {
  const byId = new Map((rows || []).map((row) => [rowIdentityKey(row), row]));
  return (ids || []).map((id) => byId.get(String(id))).filter(Boolean);
}
function filtersFromSelectedRows(rows) {
  const selected = Array.isArray(rows) ? rows : [];
  const vendors = uniq(selected.map((row) => cleanFilterValue(row.vendor)));
  const materials = uniq(selected.map((row) => cleanFilterValue(row.material)));
  const skus = uniq(selected.map((row) => cleanFilterValue(row.sku)));
  const materialIds = uniq(selected.map(realMaterialId));
  const allHaveMaterialIds =
    selected.length > 0 && materialIds.length === selected.length;
  const requestParams = {
    vendors: vendors.length ? vendors.join(",") : undefined,
    ...(allHaveMaterialIds
      ? { material_ids: materialIds.join(",") }
      : {
          materials: materials.length ? materials.join(",") : undefined,
          skus: skus.length ? skus.join(",") : undefined,
        }),
  };
  return { vendors, materials, skus, material_ids: materialIds, requestParams };
}
function exportTimestamp() {
  return formatDateTimeForExport(new Date());
}
function metadataDate(value) {
  const raw = cleanFilterValue(value);
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : raw;
}
function datePart(value) {
  return cleanFilterValue(value) || "all-dates";
}
function exportFilename(slug, dateRange, extension) {
  return `${slug}_${datePart(dateRange?.date_from)}_to_${datePart(dateRange?.date_to)}.${extension}`;
}
function parseSeriesKey(key, statusFallback = "") {
  const parts = String(key || "").split(" • ");
  return {
    vendor: parts[0] || "",
    material: parts[1] || "",
    sku: parts[2] || "",
    status: parts.length > 3 ? parts.slice(3).join(" • ") : statusFallback,
  };
}
function seriesDisplayLabel(key) {
  const info = parseSeriesKey(key);
  return [info.vendor, info.material, info.sku, info.status]
    .filter(Boolean)
    .join(" • ");
}
function buildLongRows(
  rows,
  seriesKeys,
  { statusFallback = "", includeStatus = false } = {},
) {
  const out = [];
  for (const row of rows || []) {
    for (const key of seriesKeys || []) {
      if (!(key in row)) continue;
      const info = parseSeriesKey(key, statusFallback);
      const item = {
        Period: row.bucket || "",
        Vendor: info.vendor,
        Material: info.material,
        SKU: info.sku,
        Value: row[key],
      };
      if (includeStatus) item.Status = info.status;
      out.push(item);
    }
  }
  return out;
}
function buildExportRows({
  reportName,
  dateRange,
  filters,
  statuses = [],
  dataRows,
  includeStatus = false,
  labelInfo = {},
}) {
  const vendorLabel = labelInfo.vendor || "Vendor";
  const materialLabel = labelInfo.material || "Material";
  const vendorPluralLabel = labelInfo.vendor_plural || "Vendors";
  const materialPluralLabel = labelInfo.material_plural || "Materials";
  const metadata = [
    ["Report", reportName],
    ["Date From", metadataDate(dateRange?.date_from)],
    ["Date To", metadataDate(dateRange?.date_to)],
    [vendorPluralLabel, (filters?.vendors || []).join("; ")],
    [materialPluralLabel, (filters?.materials || []).join("; ")],
    ["SKUs", (filters?.skus || []).join("; ")],
  ];
  if (includeStatus) metadata.push(["Statuses", (statuses || []).join("; ")]);
  metadata.push(["Exported At", exportTimestamp()]);

  const fieldKeys = includeStatus
    ? ["Period", "Vendor", "Material", "SKU", "Status", "Value"]
    : ["Period", "Vendor", "Material", "SKU", "Value"];
  const headers = includeStatus
    ? ["Period", vendorLabel, materialLabel, "SKU", "Status", "Value"]
    : ["Period", vendorLabel, materialLabel, "SKU", "Value"];
  return [
    ...metadata,
    [],
    headers,
    ...(dataRows || []).map((row) => fieldKeys.map((key) => row[key])),
  ];
}
function SimpleTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip-card chart-tooltip-card--minimal">
      <div>
        <b>Period:</b> {label}
      </div>
      {payload.map((entry) => (
        <div key={entry.dataKey}>
          <b>Value:</b> {entry.value}
          <span className="helper-text"> {entry.name || entry.dataKey}</span>
        </div>
      ))}
    </div>
  );
}
function ToolbarCommon({
  onApply,
  applyDisabled = false,
  sectionControls = null,
  exportControls = null,
}) {
  return (
    <div
      className="chart-toolbar filters-bar kpi-section-toolbar"
      style={{ marginTop: 8, alignItems: "flex-end" }}
    >
      {sectionControls}
      <div style={{ flex: 1 }} />
      {exportControls}
      <button
        className="btn btn-primary"
        type="button"
        onClick={onApply}
        disabled={applyDisabled}
      >
        Apply
      </button>
    </div>
  );
}
function PlaceholderCard({
  text = "Select rows and a date range in the shared KPI controls, then click Apply Selection.",
}) {
  return (
    <div
      className="table--card"
      style={{
        height: 360,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#666",
        textAlign: "center",
        padding: 16,
      }}
    >
      {text}
    </div>
  );
}
function KpiChartFrame({ title, subtitle, renderChart, footer }) {
  return (
    <div
      className="chart-block kpi-chart-frame"
      style={{ width: "100%", marginTop: 8 }}
    >
      <ExpandableChart
        title={title}
        subtitle={subtitle}
        renderChart={renderChart}
        footer={footer}
      />
    </div>
  );
}

export default function KPIDashboard() {
  const { user } = useAuth();
  const { t, tp } = useTerminology();
  const statusRows = useStatusRows(user);
  const catalogRows = useMaterialCatalog(user);
  const filteredStatusRows = React.useMemo(() => {
  const catalogMaterialIds = new Set(
    (catalogRows || []).map(realMaterialId).filter(Boolean),
  );
  return (statusRows || []).filter((row) => {
    const mid = realMaterialId(row);
    if (!mid) return true; // preserve legacy rows with no material_id
    return catalogMaterialIds.has(mid);
  });
}, [catalogRows, statusRows]);
const optionRows = React.useMemo(
  () => mergeMaterialRows(catalogRows, filteredStatusRows),
  [catalogRows, filteredStatusRows],
);

  const [selectionOpen, setSelectionOpen] = React.useState(true);
  const [draftSelectionIds, setDraftSelectionIds] = React.useState([]);
  const [appliedSelectionIds, setAppliedSelectionIds] = React.useState([]);
  const [selectionSortBy, setSelectionSortBy] = React.useState("vendor");
  const [selectionSortOrder, setSelectionSortOrder] = React.useState("asc");
  const [dateDraft, setDateDraft] = React.useState({ ...emptyDateRange });
  const [appliedDate, setAppliedDate] = React.useState({ ...emptyDateRange });
  const [activeTab, setActiveTab] = React.useState("status");
  const [exportError, setExportError] = React.useState("");

  usePrunedSelection(draftSelectionIds, setDraftSelectionIds, optionRows);
  usePrunedSelection(appliedSelectionIds, setAppliedSelectionIds, optionRows);

  const draftSelectionRows = React.useMemo(
    () => selectedRowsFromIds(optionRows, draftSelectionIds),
    [optionRows, draftSelectionIds],
  );
  const appliedSelectionRows = React.useMemo(
    () => selectedRowsFromIds(optionRows, appliedSelectionIds),
    [optionRows, appliedSelectionIds],
  );
  const appliedFilters = React.useMemo(
    () => filtersFromSelectedRows(appliedSelectionRows),
    [appliedSelectionRows],
  );
  const hasAppliedSelection = appliedSelectionRows.length > 0;
  const hasAppliedDate = dateRangeComplete(appliedDate);

  const selectionColumns = React.useMemo(
    () => [
      { key: "vendor", label: t("vendor") },
      { key: "material", label: t("material") },
      { key: "sku", label: "SKU" },
    ],
    [t],
  );
  const selectionSortOptions = React.useMemo(
    () => [
      { key: "vendor", label: t("vendor"), value: (row) => row.vendor },
      { key: "material", label: t("material"), value: (row) => row.material },
      { key: "sku", label: "SKU", value: (row) => row.sku },
    ],
    [t],
  );

  const [s1StatusDraft, setS1StatusDraft] = React.useState(["Approved"]);
  const [s1Status, setS1Status] = React.useState(["Approved"]);
  const [s1Refresh, setS1Refresh] = React.useState(0);
  const [s1Data, setS1Data] = React.useState({ rows: [], keys: [] });

  const [yRefresh, setYRefresh] = React.useState(0);
  const [yData, setYData] = React.useState({ rows: [], keys: [] });

  const [dRefresh, setDRefresh] = React.useState(0);
  const [dData, setDData] = React.useState({ rows: [], keys: [] });

  const s1Params = React.useMemo(
    () => ({
      from: appliedDate.date_from || undefined,
      to: appliedDate.date_to || undefined,
      ...appliedFilters.requestParams,
      statuses: s1Status.length ? s1Status.join(",") : undefined,
    }),
    [appliedDate, appliedFilters, s1Status],
  );
  const yParams = React.useMemo(
    () => ({
      from: appliedDate.date_from || undefined,
      to: appliedDate.date_to || undefined,
      ...appliedFilters.requestParams,
    }),
    [appliedDate, appliedFilters],
  );
  const dParams = React.useMemo(
    () => ({
      from: appliedDate.date_from || undefined,
      to: appliedDate.date_to || undefined,
      ...appliedFilters.requestParams,
    }),
    [appliedDate, appliedFilters],
  );

  const s1Ready = Boolean(
    hasAppliedSelection && hasAppliedDate && s1Status.length,
  );
  const yReady = Boolean(hasAppliedSelection && hasAppliedDate);
  const dReady = Boolean(hasAppliedSelection && hasAppliedDate);

  useDebouncedFetch(
    s1Ready,
    [s1Ready, JSON.stringify(s1Params), s1Refresh],
    (signal) => {
      api
        .get("/kpi/status_timeseries", { params: s1Params, signal })
        .then((res) =>
          setS1Data({ rows: res.data?.rows || [], keys: res.data?.keys || [] }),
        )
        .catch(() => setS1Data({ rows: [], keys: [] }));
    },
  );
  useDebouncedFetch(
    yReady,
    [yReady, JSON.stringify(yParams), yRefresh],
    (signal) => {
      api
        .get("/kpi/yield_timeseries", { params: yParams, signal })
        .then((res) =>
          setYData({ rows: res.data?.rows || [], keys: res.data?.keys || [] }),
        )
        .catch(() => setYData({ rows: [], keys: [] }));
    },
  );
  useDebouncedFetch(
    dReady,
    [dReady, JSON.stringify(dParams), dRefresh],
    (signal) => {
      api
        .get("/kpi/deviation_timeseries", { params: dParams, signal })
        .then((res) =>
          setDData({ rows: res.data?.rows || [], keys: res.data?.keys || [] }),
        )
        .catch(() => setDData({ rows: [], keys: [] }));
    },
  );

  const s1HasData = s1Data.rows.length > 0 && s1Data.keys.length > 0;
  const yHasData = yData.rows.length > 0 && yData.keys.length > 0;
  const dHasData = dData.rows.length > 0 && dData.keys.length > 0;
  const statusFallback = s1Status.length === 1 ? s1Status[0] : "";

  const selectionPrompt = !hasAppliedSelection
    ? "Select rows and a date range from the shared KPI controls, then click Apply Selection."
    : hasAppliedDate
      ? `${appliedSelectionRows.length} row${appliedSelectionRows.length === 1 ? "" : "s"} and ${metadataDate(appliedDate.date_from)}–${metadataDate(appliedDate.date_to)} applied to all KPI sections.`
      : `${appliedSelectionRows.length} row${appliedSelectionRows.length === 1 ? "" : "s"} applied. Select a shared KPI date range and click Apply Selection.`;

  const csvLabelInfo = React.useMemo(
    () => ({
      vendor: t("vendor"),
      vendor_plural: tp("vendor"),
      material: t("material"),
      material_plural: tp("material"),
    }),
    [t, tp],
  );

  const exportStatus = async (extension) => {
    const rows = buildLongRows(s1Data.rows, s1Data.keys, {
      statusFallback,
      includeStatus: true,
    });
    const exportRows = buildExportRows({
      reportName: "KPI by Status",
      dateRange: appliedDate,
      filters: appliedFilters,
      statuses: s1Status,
      dataRows: rows,
      includeStatus: true,
      labelInfo: csvLabelInfo,
    });
    setExportError("");
    try {
      if (extension === "xlsx") {
        await downloadExcelRows(
          exportFilename("kpi_status", appliedDate, extension),
          exportRows,
          { sheetName: "KPI Status" },
        );
      } else {
        downloadCsvRows(
          exportFilename("kpi_status", appliedDate, extension),
          exportRows,
        );
      }
    } catch (_) {
      setExportError("Could not create the KPI Status export.");
    }
  };
  const exportYield = async (extension) => {
    const rows = buildLongRows(yData.rows, yData.keys);
    const exportRows = buildExportRows({
      reportName: "KPI by Yield",
      dateRange: appliedDate,
      filters: appliedFilters,
      dataRows: rows,
      labelInfo: csvLabelInfo,
    });
    setExportError("");
    try {
      if (extension === "xlsx") {
        await downloadExcelRows(
          exportFilename("kpi_yield", appliedDate, extension),
          exportRows,
          { sheetName: "KPI Yield" },
        );
      } else {
        downloadCsvRows(
          exportFilename("kpi_yield", appliedDate, extension),
          exportRows,
        );
      }
    } catch (_) {
      setExportError("Could not create the KPI Yield export.");
    }
  };
  const exportDeviation = async (extension) => {
    const rows = buildLongRows(dData.rows, dData.keys);
    const exportRows = buildExportRows({
      reportName: "KPI by Deviations",
      dateRange: appliedDate,
      filters: appliedFilters,
      dataRows: rows,
      labelInfo: csvLabelInfo,
    });
    setExportError("");
    try {
      if (extension === "xlsx") {
        await downloadExcelRows(
          exportFilename("kpi_deviations", appliedDate, extension),
          exportRows,
          { sheetName: "KPI Deviations" },
        );
      } else {
        downloadCsvRows(
          exportFilename("kpi_deviations", appliedDate, extension),
          exportRows,
        );
      }
    } catch (_) {
      setExportError("Could not create the KPI Deviations export.");
    }
  };

  const statusDetailsRows = React.useMemo(
    () =>
      buildLongRows(s1Data.rows, s1Data.keys, {
        statusFallback,
        includeStatus: true,
      }).map((row, idx) => ({
        id: idx,
        vendor: row.Vendor,
        material: row.Material,
        sku: row.SKU,
        status: row.Status,
        period: row.Period,
        value: row.Value,
      })),
    [s1Data, statusFallback],
  );
  const statusDetailsButton = (
    <DetailsModal
      title="KPI by Status Details"
      columns={[
        { key: "vendor", label: t("vendor") },
        { key: "material", label: t("material") },
        { key: "sku", label: "SKU" },
        { key: "status", label: "Status" },
        { key: "period", label: "Period" },
        { key: "value", label: "Value" },
      ]}
      rows={statusDetailsRows}
      emptyText="No KPI by Status details available for the current filters."
    />
  );

  const StatusSelector = (
    <div className="field" style={{ minWidth: 240 }}>
      <label>Status</label>
      <Select
        isMulti
        options={STATUSES.map((s) => ({ value: s, label: s }))}
        value={s1StatusDraft.map((s) => ({ value: s, label: s }))}
        onChange={(list) => setS1StatusDraft((list || []).map((x) => x.value))}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        styles={rsStyles}
      />
    </div>
  );

  const applySelectionAndDate = () => {
    setAppliedSelectionIds(draftSelectionIds);
    setAppliedDate(dateDraft);
    if (draftSelectionIds.length && dateRangeComplete(dateDraft))
      setSelectionOpen(false);
  };

  const renderStatusChart = ({ expanded }) => (
    <div
      className={
        expanded ? "kpi-chart-size kpi-chart-size--expanded" : "kpi-chart-size"
      }
    >
      <ResponsiveContainer>
        <BarChart data={s1Data.rows}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="bucket" interval="preserveStartEnd" tickMargin={8} />
          <YAxis allowDecimals={false} />
          <Tooltip content={<SimpleTooltip />} />
          {expanded ? <Legend /> : null}
          {s1Data.keys.map((k, i) => (
            <Bar
              key={k}
              dataKey={k}
              name={seriesDisplayLabel(k)}
              fill={pickColor(i)}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
  const renderYieldChart = ({ expanded }) => (
    <div
      className={
        expanded ? "kpi-chart-size kpi-chart-size--expanded" : "kpi-chart-size"
      }
    >
      <ResponsiveContainer>
        <LineChart data={yData.rows}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="bucket" interval="preserveStartEnd" tickMargin={8} />
          <YAxis />
          <Tooltip content={<SimpleTooltip />} />
          {expanded ? <Legend /> : null}
          {yData.keys.map((k, i) => (
            <Line
              key={k}
              type="monotone"
              dataKey={k}
              name={seriesDisplayLabel(k)}
              dot
              activeDot={{ r: 4 }}
              stroke={pickColor(i)}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
  const renderDeviationChart = ({ expanded }) => (
    <div
      className={
        expanded ? "kpi-chart-size kpi-chart-size--expanded" : "kpi-chart-size"
      }
    >
      <ResponsiveContainer>
        <BarChart data={dData.rows}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="bucket" interval="preserveStartEnd" tickMargin={8} />
          <YAxis allowDecimals={false} />
          <Tooltip content={<SimpleTooltip />} />
          {expanded ? <Legend /> : null}
          {dData.keys.map((k, i) => (
            <Bar
              key={k}
              dataKey={k}
              name={seriesDisplayLabel(k)}
              fill={pickColor(i)}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  const placeholderText = !hasAppliedSelection
    ? selectionPrompt
    : !hasAppliedDate
      ? "Select a shared KPI date range and click Apply Selection."
      : "Set the section controls and click Apply.";

  return (
    <div className="section kpi-dashboard-page">
      <h2 style={{ marginTop: 0 }}>KPI Dashboard</h2>
      {exportError ? <div className="banner error">{exportError}</div> : null}

      <details
        className="selection-expander kpi-selection-expander"
        open={selectionOpen}
        onToggle={(event) => setSelectionOpen(event.currentTarget.open)}
      >
        <summary>
          Select {t("vendor")} / {t("material")} / SKU for KPI
        </summary>
        <div className="helper-text" style={{ marginBottom: 10 }}>
          {selectionPrompt}
        </div>
        <LotSelectionTable
          rows={optionRows}
          columns={selectionColumns}
          sortOptions={selectionSortOptions}
          sortBy={selectionSortBy}
          onSortByChange={setSelectionSortBy}
          sortOrder={selectionSortOrder}
          onSortOrderChange={setSelectionSortOrder}
          selectionMode="multi"
          selected={draftSelectionIds}
          onSelectionChange={setDraftSelectionIds}
          getRowId={rowIdentityKey}
          showOrderBadges={true}
          showSelectAll={true}
          searchPlaceholder={`Search ${t("vendor").toLowerCase()}, ${t("material").toLowerCase()}, or SKU`}
          emptyText={`No ${t("material").toLowerCase()}/SKU rows found.`}
          pageSize={5}
        />
        <div className="kpi-shared-controls filters-bar">
          <DateRangePicker
            value={dateDraft}
            onChange={setDateDraft}
            onClear={(empty) => {
              setDateDraft(empty);
              setAppliedDate(empty);
            }}
            label="Shared KPI date range"
          />
          <div style={{ flex: 1 }} />
          <div className="helper-text">
            Draft selection: {draftSelectionRows.length} row
            {draftSelectionRows.length === 1 ? "" : "s"}
          </div>
          <button
            className="btn btn-secondary"
            type="button"
            onClick={() => {
              setDraftSelectionIds([]);
              setAppliedSelectionIds([]);
              setAppliedDate({ ...emptyDateRange });
              setSelectionOpen(true);
            }}
            disabled={
              !draftSelectionIds.length &&
              !appliedSelectionIds.length &&
              !dateDraft.date_from &&
              !dateDraft.date_to
            }
          >
            Clear Selection
          </button>
          <button
            className="btn btn-primary"
            type="button"
            onClick={applySelectionAndDate}
            disabled={
              !draftSelectionIds.length || !dateRangeComplete(dateDraft)
            }
          >
            Apply Selection
          </button>
        </div>
      </details>

      <div
        className="notifications-page-tabs kpi-tabs"
        role="tablist"
        aria-label="KPI sections"
      >
        {KPI_TABS.map((tab) => (
          <button
            key={tab.key}
            className={`btn ${activeTab === tab.key ? "btn-primary" : "btn-secondary"}`}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            role="tab"
            aria-selected={activeTab === tab.key}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "status" ? (
        <div className="section kpi-tab-panel" style={{ marginTop: 12 }}>
          <ToolbarCommon
            sectionControls={StatusSelector}
            applyDisabled={
              !hasAppliedSelection || !hasAppliedDate || !s1StatusDraft.length
            }
            exportControls={
              <div className="hstack">
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => exportStatus("csv")}
                  disabled={!s1Ready}
                >
                  Download CSV
                </button>
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => exportStatus("xlsx")}
                  disabled={!s1Ready}
                >
                  Download Excel
                </button>
              </div>
            }
            onApply={() => {
              setS1Status(s1StatusDraft);
              setS1Refresh((value) => value + 1);
            }}
          />
          {s1Ready && s1HasData ? (
            <KpiChartFrame
              title="KPI by Status"
              subtitle={selectionPrompt}
              renderChart={renderStatusChart}
              footer={<div className="hstack">{statusDetailsButton}</div>}
            />
          ) : (
            <PlaceholderCard
              text={
                s1Ready
                  ? "No status data exists for the selected filters/date range."
                  : placeholderText
              }
            />
          )}
        </div>
      ) : null}

      {activeTab === "yield" ? (
        <div className="section kpi-tab-panel" style={{ marginTop: 12 }}>
          <ToolbarCommon
            applyDisabled={!yReady}
            exportControls={
              <div className="hstack">
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => exportYield("csv")}
                  disabled={!yReady}
                >
                  Download CSV
                </button>
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => exportYield("xlsx")}
                  disabled={!yReady}
                >
                  Download Excel
                </button>
              </div>
            }
            onApply={() => setYRefresh((value) => value + 1)}
          />
          {yReady && yHasData ? (
            <KpiChartFrame
              title="KPI by Yield"
              subtitle={selectionPrompt}
              renderChart={renderYieldChart}
            />
          ) : (
            <PlaceholderCard
              text={
                yReady
                  ? "No checklist-derived yield data exists for the selected filters/date range."
                  : placeholderText
              }
            />
          )}
        </div>
      ) : null}

      {activeTab === "deviations" ? (
        <div className="section kpi-tab-panel" style={{ marginTop: 12 }}>
          <ToolbarCommon
            applyDisabled={!dReady}
            exportControls={
              <div className="hstack">
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => exportDeviation("csv")}
                  disabled={!dReady}
                >
                  Download CSV
                </button>
                <button
                  className="btn btn-secondary"
                  type="button"
                  onClick={() => exportDeviation("xlsx")}
                  disabled={!dReady}
                >
                  Download Excel
                </button>
              </div>
            }
            onApply={() => setDRefresh((value) => value + 1)}
          />
          {dReady && dHasData ? (
            <KpiChartFrame
              title="KPI by Deviations"
              subtitle={selectionPrompt}
              renderChart={renderDeviationChart}
            />
          ) : (
            <PlaceholderCard
              text={
                dReady
                  ? "No checklist deviation data exists for the selected filters/date range."
                  : placeholderText
              }
            />
          )}
        </div>
      ) : null}
    </div>
  );
}
